import React, { useState, useEffect, useCallback } from 'react';
import { OperaSection } from './OperaWindow';
import { useAuth } from '../lib/auth';
import {
  api,
  type ProfileNote,
  type StayNote,
  type ReservationDeclarationNote,
  type ReservationHistoryEvent,
} from '../lib/api';

/**
 * Notes module on the profile panel — three read-mostly views of what staff
 * know about a provider.
 *
 *   Notes         free text written by staff. Reception and up read and write;
 *                 MANAGER+ deletes with a reason; ADMIN can purge outright.
 *                 Immutable once written — the correction path is a follow-up
 *                 note, not a silent rewrite.
 *   Reservations  Reservation.notes from bookings she was attached to.
 *   Shifts        declaration notes plus her call-off / reinstatement history,
 *                 each showing who recorded it.
 *
 * Everything here is staff-facing. None of it is reachable from the provider
 * portal, which is why the caption says so out loud: people write differently
 * depending on who they think is reading.
 */

type SubTab = 'notes' | 'stays' | 'shifts';

interface Props {
  profileId: string;
  workingName: string;
  onError: (msg: string | null) => void;
}

export function ProfileNotesTab({ profileId, workingName, onError }: Props) {
  const [tab, setTab] = useState<SubTab>('notes');

  return (
    <>
      <div className="notes-subtabs" role="tablist">
        {([
          { key: 'notes',        label: 'Notes' },
          { key: 'stays', label: 'Reservations' },
          { key: 'shifts',       label: 'Shifts' },
        ] as Array<{ key: SubTab; label: string }>).map(t => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={`notes-subtab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="notes-caption">
        Staff-only. Nothing here is visible to {workingName} in the provider portal.
      </div>

      {tab === 'notes' && <NotesList profileId={profileId} onError={onError} />}
      {tab === 'stays' && <ReservationNotesList profileId={profileId} onError={onError} />}
      {tab === 'shifts' && <ShiftNotesList profileId={profileId} onError={onError} />}
    </>
  );
}

// --- Sub-tab 1: staff notes -------------------------------------------------

function NotesList({ profileId, onError }: { profileId: string; onError: (m: string | null) => void }) {
  const { user } = useAuth();
  const canDelete = user?.role === 'MANAGER' || user?.role === 'ADMIN';
  const canPurge = user?.role === 'ADMIN';

  const [notes, setNotes] = useState<ProfileNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<{ note: ProfileNote; mode: 'delete' | 'purge' } | null>(null);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.profileNotes(profileId);
      setNotes(res.notes);
      onError(null);
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [profileId, onError]);

  useEffect(() => { load(); }, [load]);

  async function handleAdd() {
    if (!draft.trim() || busy) return;
    setBusy(true);
    try {
      await api.addProfileNote(profileId, draft.trim());
      setDraft('');
      await load();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove() {
    if (!removing || !reason.trim() || busy) return;
    setBusy(true);
    try {
      if (removing.mode === 'purge') {
        await api.purgeProfileNote(profileId, removing.note.id, reason.trim());
      } else {
        await api.deleteProfileNote(profileId, removing.note.id, reason.trim());
      }
      setRemoving(null);
      setReason('');
      await load();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (removing) {
    const isPurge = removing.mode === 'purge';
    return (
      <OperaSection title={isPurge ? 'Purge note permanently' : 'Delete note'}>
        <p style={{ margin: '4px 0 10px', fontSize: 12 }}>
          {isPurge ? (
            <>
              This <strong>destroys the text permanently</strong>. A record that a
              note existed and was purged is kept — who wrote it, who removed it
              and why — but not what it said. Use this only when the content
              itself has to be gone.
            </>
          ) : (
            <>
              The note is removed from this list. It stays in the database with
              your name, the time and the reason, so the removal is on record.
            </>
          )}
        </p>
        <div className="notes-entry" style={{ marginBottom: 10 }}>
          <div className="notes-entry-head">
            <span className="notes-entry-author">{removing.note.author}</span>
            <span className="notes-entry-date">{formatStamp(removing.note.createdAt)}</span>
          </div>
          <div className="notes-entry-body">{removing.note.body}</div>
        </div>
        <label className="notes-reason-label">
          Reason (required)
          <input
            type="text"
            value={reason}
            onChange={e => setReason(e.target.value)}
            maxLength={200}
            autoFocus
            placeholder={isPurge ? 'e.g. legal request, written in error' : 'e.g. duplicate, inaccurate'}
          />
        </label>
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <button
            type="button"
            className="avail-button-reject"
            onClick={handleRemove}
            disabled={busy || !reason.trim()}
          >
            {isPurge ? 'Purge permanently' : 'Delete note'}
          </button>
          <button
            type="button"
            className="avail-add-button"
            onClick={() => { setRemoving(null); setReason(''); }}
            disabled={busy}
          >
            Back
          </button>
        </div>
      </OperaSection>
    );
  }

  return (
    <>
      <OperaSection title="Add note">
        <textarea
          className="notes-composer"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder="What happened? Keep it factual — she can request this under privacy law."
          maxLength={2000}
          rows={3}
          disabled={busy}
        />
        <div className="notes-composer-actions">
          <span className="notes-charcount">{draft.length}/2000</span>
          <button
            type="button"
            className="avail-add-button"
            onClick={handleAdd}
            disabled={busy || !draft.trim()}
          >
            Save note
          </button>
        </div>
      </OperaSection>

      <div className="notes-list">
        {loading && <div className="avail-empty">Loading…</div>}
        {!loading && notes.length === 0 && (
          <div className="avail-empty">No notes yet.</div>
        )}
        {!loading && notes.map(n => (
          <div key={n.id} className="notes-entry">
            <div className="notes-entry-head">
              <span className="notes-entry-author">{n.author}</span>
              <span className="notes-entry-date">{formatStamp(n.createdAt)}</span>
              {canDelete && (
                <span className="notes-entry-actions">
                  <button
                    type="button"
                    className="avail-link-danger"
                    onClick={() => setRemoving({ note: n, mode: 'delete' })}
                  >Delete</button>
                  {canPurge && (
                    <>
                      {' · '}
                      <button
                        type="button"
                        className="avail-link-danger"
                        onClick={() => setRemoving({ note: n, mode: 'purge' })}
                      >Purge</button>
                    </>
                  )}
                </span>
              )}
            </div>
            <div className="notes-entry-body">{n.body}</div>
          </div>
        ))}
      </div>
    </>
  );
}

// --- Sub-tab 2: stay notes -------------------------------------------

function ReservationNotesList({ profileId, onError }: { profileId: string; onError: (m: string | null) => void }) {
  const [rows, setRows] = useState<StayNote[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await api.profileReservationNotes(profileId);
        if (live) { setRows(res.stays); onError(null); }
      } catch (e) {
        if (live) onError(e instanceof Error ? e.message : String(e));
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [profileId, onError]);

  if (loading) return <div className="avail-empty">Loading…</div>;
  if (rows.length === 0) return <div className="avail-empty">No booking notes for this individual.</div>;

  return (
    <div className="notes-list">
      {rows.map(r => (
        <div key={r.id} className="notes-entry is-readonly">
          <div className="notes-entry-head">
            <span className="notes-entry-author">
              {r.arrivalDate} → {r.departureDate}
            </span>
            <span className="notes-entry-date">
              {r.roomLabel} · {r.status}{!r.wasPrimary && ' · occupant'}
            </span>
          </div>
          <div className="notes-entry-body">{r.notes}</div>
        </div>
      ))}
    </div>
  );
}

// --- Sub-tab 3: shift notes and history -------------------------------------

function ShiftNotesList({ profileId, onError }: { profileId: string; onError: (m: string | null) => void }) {
  const [declarations, setDeclarations] = useState<ReservationDeclarationNote[]>([]);
  const [events, setEvents] = useState<ReservationHistoryEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await api.profileShiftNotes(profileId);
        if (live) { setDeclarations(res.declarations); setEvents(res.events); onError(null); }
      } catch (e) {
        if (live) onError(e instanceof Error ? e.message : String(e));
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [profileId, onError]);

  if (loading) return <div className="avail-empty">Loading…</div>;

  const callOffs = events.filter(e => e.toStatus === 'CALLED_OFF').length;

  return (
    <>
      <OperaSection title="Reservation history">
        {events.length === 0 ? (
          <div className="avail-empty">No recorded reservation history.</div>
        ) : (
          <>
            {callOffs > 0 && (
              <div className="notes-tally">
                {callOffs} call-off{callOffs === 1 ? '' : 's'} on record
              </div>
            )}
            <div className="notes-list">
              {events.map(e => (
                <div key={e.id} className="notes-entry is-readonly">
                  <div className="notes-entry-head">
                    <span className="notes-entry-author">{describeEvent(e)}</span>
                    <span className="notes-entry-date">{formatStamp(e.performedAt)}</span>
                  </div>
                  <div className="notes-entry-body">
                    Shift of {e.date} · recorded by {e.performedBy}
                    {e.notes ? ` — ${e.notes}` : ''}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </OperaSection>

      <OperaSection title="Declaration notes">
        {declarations.length === 0 ? (
          <div className="avail-empty">No notes on her declarations.</div>
        ) : (
          <div className="notes-list">
            {declarations.map(d => (
              <div key={d.id} className="notes-entry is-readonly">
                <div className="notes-entry-head">
                  <span className="notes-entry-author">
                    {d.date}{d.shiftTimes ? ` · ${d.shiftTimes}` : ''}
                  </span>
                  <span className="notes-entry-date">{d.mode} · {d.status}</span>
                </div>
                <div className="notes-entry-body">
                  {[
                    d.approvalNotes && `Approval: ${d.approvalNotes}${d.approvedBy ? ` (${d.approvedBy})` : ''}`,
                    d.cancelNotes && `Cancellation: ${d.cancelNotes}${d.cancelledBy ? ` (${d.cancelledBy})` : ''}`,
                    d.cancelReason && `Reason: ${d.cancelReason.toLowerCase().replace(/_/g, ' ')}`,
                    d.unavailableReason && `Unavailable: ${d.unavailableReason}`,
                    d.rejectedReason && `Rejected: ${d.rejectedReason}`,
                    d.slotNotes && `Roster note: ${d.slotNotes}`,
                  ].filter(Boolean).join(' · ')}
                </div>
              </div>
            ))}
          </div>
        )}
      </OperaSection>
    </>
  );
}

// --- Helpers ----------------------------------------------------------------

function describeEvent(e: ReservationHistoryEvent): string {
  if (e.toStatus === 'CALLED_OFF') {
    return e.reason
      ? `Called off — ${e.reason.toLowerCase().replace(/_/g, ' ')}`
      : 'Called off';
  }
  if (e.fromStatus === 'CALLED_OFF' && e.toStatus === 'CONFIRMED') return 'Reinstated';
  return `${e.fromStatus} → ${e.toStatus}`.toLowerCase().replace(/_/g, ' ');
}

function formatStamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('en-AU', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
}
